import { Route, populateRoute } from "@repo/common"
import { CreateStandardOffering, StandardOffering } from "@repo/common"
import {
  UseMutationOptions,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { request } from "../api"

async function fetchStandardOfferings(
  storeId: string,
): Promise<StandardOffering[]> {
  return request<StandardOffering[]>({
    url: populateRoute(
      Route.STANDARD_OFFERING_CONTROLLER__FIND_MANY_BY_STORE_ID,
      storeId,
    ),
    method: "GET",
  })
}

async function createStandardOffering(
  storeId: string,
  data: CreateStandardOffering,
): Promise<StandardOffering> {
  return request<StandardOffering>({
    url: populateRoute(Route.STANDARD_OFFERING_CONTROLLER__CREATE, storeId),
    method: "POST",
    data,
  })
}

async function deleteStandardOffering(id: string): Promise<void> {
  return request<void>({
    url: populateRoute(Route.STANDARD_OFFERING_CONTROLLER__DELETE, id),
    method: "DELETE",
  })
}

export const useCreateStandardOffering = (
  storeId: string,
  options?: UseMutationOptions<
    StandardOffering[],
    Error,
    CreateStandardOffering[]
  >,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateStandardOffering[]) => {
      const promises = data.map((so) => createStandardOffering(storeId, so))
      return Promise.all(promises)
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [standardOfferingQueries.list(storeId).queryKey],
      }),
    ...options,
  })
}

export const useDeleteStandardOffering = (
  storeId: string,
  options?: UseMutationOptions<void[], Error, string[]>,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ids: string[]) => {
      const promises = ids.map((id) => deleteStandardOffering(id))
      return Promise.all(promises)
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [standardOfferingQueries.list(storeId).queryKey],
      }),
    ...options,
  })
}

const standardOfferingQueries = {
  all: () => ["standard-offering"],
  lists: () => [...standardOfferingQueries.all(), "list"],
  list: (storeId: string) =>
    queryOptions({
      queryKey: [...standardOfferingQueries.lists(), storeId],
      queryFn: () => fetchStandardOfferings(storeId),
    }),
}

export default standardOfferingQueries
